
import { getSupabaseClient } from "@/integrations/supabase/client";
import { getSafeClient } from "@/integrations/supabase/safeClient";
import { ProfileQueryResult, SellerProfileQueryResult, PublicProfile, SellerProfile } from "./types";
import { toast } from "@/components/ui/use-toast";

/**
 * Fetch a profile by user ID
 */
export async function fetchProfileById(userId: string): Promise<PublicProfile | null> {
  try {
    if (!userId) return null;
    
    const supabaseClient = await getSafeClient();
    
    const { data, error } = await supabaseClient
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
      
    if (error) {
      console.error("Error fetching profile:", error);
      throw error;
    }
    
    if (!data) return null;
    
    return {
      id: data.id,
      name: data.name,
      avatar_url: data.avatar_url,
      bio: data.bio || null,
      created_at: data.created_at,
      seller_profile_id: data.seller_profile_id || null
    } as PublicProfile;
  } catch (err) {
    console.error("Error in fetchProfileById:", err);
    toast({
      title: "Error",
      description: "Failed to load user profile",
      variant: "destructive",
    });
    throw err;
  }
}

/**
 * Fetch a seller profile by ID or slug
 */
export async function fetchSellerProfile(idOrSlug: string): Promise<SellerProfile | null> {
  try {
    if (!idOrSlug) return null;
    
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrSlug);
    const queryField = isUuid ? "id" : "slug";
    
    const supabaseClient = await getSafeClient();
    
    const { data, error } = await supabaseClient
      .from("seller_profiles")
      .select("*")
      .eq(queryField, idOrSlug)
      .single();
      
    if (error) {
      console.error(`Error fetching seller profile with ${queryField}=${idOrSlug}:`, error);
      return null;
    }
    
    if (!data) return null;
    
    return data as SellerProfile;
  } catch (err) {
    console.error("Error in fetchSellerProfile:", err);
    return null;
  }
}

/**
 * Update a seller profile
 */
export async function updateSellerProfile(
  sellerId: string, 
  profileData: Partial<SellerProfile>
): Promise<SellerProfile | null> {
  try {
    if (!sellerId) return null;
    
    // Only allow specific fields to be updated
    const allowedFields = ['name', 'bio', 'website_url', 'logo_url'];
    
    // Filter out any fields that shouldn't be updated
    const sanitizedData = Object.keys(profileData)
      .filter(key => allowedFields.includes(key))
      .reduce((obj, key) => {
        obj[key] = profileData[key as keyof Partial<SellerProfile>];
        return obj;
      }, {} as Record<string, any>);
      
    const supabaseClient = await getSafeClient();
    
    const { data, error } = await supabaseClient
      .from("seller_profiles")
      .update(sanitizedData)
      .eq("id", sellerId)
      .select()
      .single();
      
    if (error) {
      console.error("Error updating seller profile:", error);
      throw error;
    }
    
    if (!data) return null;
    
    return data as SellerProfile;
  } catch (err) {
    console.error("Error in updateSellerProfile:", err);
    toast({
      title: "Error",
      description: "Failed to update seller profile",
      variant: "destructive",
    });
    throw err;
  }
}

/**
 * Fetch all seller profiles with basic filtering
 */
export async function fetchSellerProfiles(
  options: { 
    limit?: number; 
    verified?: boolean;
    sortBy?: 'name' | 'created_at';
    order?: 'asc' | 'desc';
  } = {}
): Promise<SellerProfile[]> {
  try {
    const { 
      limit = 100, 
      verified = undefined,
      sortBy = 'created_at',
      order = 'desc'
    } = options;
    
    const supabaseClient = await getSafeClient();
    
    let query = supabaseClient
      .from("seller_profiles")
      .select("*")
      .order(sortBy, { ascending: order === 'asc' })
      .limit(limit);
      
    // Add verified filter if defined
    if (verified !== undefined) {
      query = query.eq("verified", verified);
    }
    
    const { data, error } = await query;
      
    if (error) {
      console.error("Error fetching seller profiles:", error);
      throw error;
    }
    
    return (data || []) as SellerProfile[];
  } catch (err) {
    console.error("Error in fetchSellerProfiles:", err);
    return [];
  }
}
